import * as React from "react";
import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { trTR } from "@mui/x-data-grid/locales";
import i18next from "i18next";
import TableLoadingOverlay from "./TableLoadingOverlay";
import CustomPagination from "./CustomPagination";
import {
  dataGridScrollContainerSx,
  dataGridSurfaceSx,
  dataGridTableSx,
} from "../../constants/dataGridStyles";
import {
  getDataGridColumnsMinWidth,
  prepareDataGridColumnsForLayout,
} from "../../utils/dataGridLayout";

const PAGE_SIZE = 15;

export default function DataTable({
  rows,
  columns,
  pageSize = PAGE_SIZE,
  page = 0,
  pagination,
  paginationModel,
  onPaginationModelChange,
  isLoading = false,
  ...props
}) {
  const rowNumberColumn = useMemo(
    () => ({
      field: "__rowNum",
      headerName: "#",
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (value, row, colDef, apiRef) => {
        const api = apiRef?.current;
        if (!api || row?.id == null) return "";
        const idx = api.getRowIndexRelativeToVisibleRows(row.id);
        if (idx == null || idx < 0) return "";
        const pm = api.state.pagination?.paginationModel ?? { page: 0, pageSize: 15 };
        return pm.page * (pm.pageSize ?? 15) + idx + 1;
      },
    }),
    []
  );

  const safeColumns = useMemo(() => {
    if (!Array.isArray(columns)) return [];
    return columns.map((column) => {
      if (!column || typeof column !== "object" || typeof column.valueGetter !== "function") {
        return column;
      }
      const originalValueGetter = column.valueGetter;
      return {
        ...column,
        valueGetter: (params, event) => {
          if (!params) return "";
          try {
            return originalValueGetter(params, event);
          } catch (err) {
            console.warn(`[DataTable] valueGetter failed for "${column.field}":`, err);
            return "";
          }
        },
      };
    });
  }, [columns]);

  const layoutColumns = useMemo(
    () => prepareDataGridColumnsForLayout(safeColumns),
    [safeColumns]
  );

  const gridColumns = useMemo(
    () => [rowNumberColumn, ...layoutColumns],
    [rowNumberColumn, layoutColumns]
  );

  // Dar ekran scroll eşiği; geniş ekranda width:100% ile alan dolar
  const minContentWidth = useMemo(
    () => getDataGridColumnsMinWidth(gridColumns),
    [gridColumns]
  );

  const [internalPaginationModel, setInternalPaginationModel] = useState({
    pageSize,
    page,
  });
  const currentPaginationModel = paginationModel || internalPaginationModel;

  const loadingOverlay = useMemo(() => {
    const columnCount = gridColumns.length;
    const rowCount = currentPaginationModel?.pageSize || pageSize || PAGE_SIZE;
    return () => <TableLoadingOverlay columnCount={columnCount} rowCount={rowCount} />;
  }, [gridColumns.length, currentPaginationModel?.pageSize, pageSize]);

  const filteredProps = { ...props };
  delete filteredProps.focusElementRef;
  delete filteredProps.disableMobileCards;
  const mergedSlots = {
    ...(filteredProps.slots || {}),
    loadingOverlay,
  };
  delete filteredProps.slots;

  const hasRows = Array.isArray(rows) && rows.length > 0;
  const rawLoading = filteredProps.loading ?? isLoading;
  const loading = Boolean(rawLoading) && !hasRows;
  delete filteredProps.loading;

  const { sx: userSx, ...restFiltered } = filteredProps;
  delete restFiltered.autoHeight;

  const handlePaginationModelChange = React.useCallback(
    (newModel) => {
      if (paginationModel) {
        onPaginationModelChange?.(newModel);
      } else {
        setInternalPaginationModel(newModel);
      }
    },
    [paginationModel, onPaginationModelChange]
  );

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <Box sx={dataGridScrollContainerSx}>
      <Box
        sx={{
          width: "100%",
          minWidth: minContentWidth,
        }}
      >
        <DataGrid
          localeText={
            i18next.language === "tr"
              ? trTR.components.MuiDataGrid.defaultProps.localeText
              : undefined
          }
          checkboxSelection={false}
          onPaginationModelChange={handlePaginationModelChange}
          paginationModel={currentPaginationModel}
          pageSizeOptions={[20]}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnResize
          autoHeight
          rows={safeRows}
          columns={gridColumns}
          loading={loading}
          slots={{
            pagination: pagination || CustomPagination,
            ...mergedSlots,
          }}
          sx={[
            dataGridSurfaceSx,
            dataGridTableSx,
            {
              width: "100%",
              minWidth: minContentWidth,
              "& .MuiDataGrid-main": { width: "100%" },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden !important",
              },
              "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
                display: "none",
              },
            },
            userSx,
          ]}
          {...restFiltered}
          autoHeight
        />
      </Box>
    </Box>
  );
}
